// Ledger-effect preview math (plan.md G20; BUILD_SPEC.md §7 "review computed
// payable (basis math shown transparently)"). Wraps `@zebra/core`'s `payableCents`
// — the EXACT pure function services/api/src/modules/intake/goods-receipts.ts
// calls at POST time — so the number an operator reviews before posting is
// guaranteed to match what the server will compute, never a second, hand-rolled
// re-implementation that could quietly drift from the real posting math.
import { CoreError, dryKg, eachPayableCents, payableCents, type MoistureBand, type PricingBasis } from '@zebra/core';
import type { WireMoistureBand } from './api';

/** Converts a wire-serialized band list (`priceCents` as a decimal string —
 * services/api's `serializeContract`/`serializePoLine`) back into `@zebra/core`'s
 * input shape (`priceCents` as `bigint`) — the client-side mirror of
 * `modules/intake/lib.ts`'s `toCoreBands`. */
export function toCoreBands(bands: WireMoistureBand[] | null | undefined): MoistureBand[] | undefined {
  if (!bands) return undefined;
  return bands.map((b) => ({ mcMin: b.mcMin, mcMax: b.mcMax, priceCents: BigInt(b.priceCents) }));
}

export interface LedgerPreviewInput {
  basis: PricingBasis;
  /** Net (wet) kg — a decimal string, e.g. from a weighbridge ticket's `netKg`. */
  netKg: string;
  mcPct: string | null;
  priceCentsPerTonne: string | null;
  bands: WireMoistureBand[] | null;
}

export interface LedgerPreviewResult {
  payableCents: bigint;
  usedPriceCents: bigint;
  /** `wetKg * (1 - mc/100)` (§9.2) — shown alongside wet kg per spec principle #2
   * ("as-weighed quantity, dry-mass analytics... never hidden"). */
  dryKg: string;
}

// `code` mirrors `@zebra/core`'s `CoreError.code` (e.g. 'MOISTURE_OUT_OF_BAND',
// 'MISSING_MOISTURE') so a caller can distinguish "the reading is outside
// every band — an ADMIN can override this" from every other blocking reason
// (missing reading, missing contract price, mismatched PO line) without
// string-matching `error`, which is display-only. Absent for the
// non-`CoreError` fallback branch (added for the G-WebA ADMIN moisture-band
// override control, 2026-07-07).
export type LedgerPreviewOutcome = { ok: true; result: LedgerPreviewResult } | { ok: false; error: string; code?: string };

/**
 * Computes the exact payable + dry-mass preview, or a human-readable blocking
 * message when the inputs can't yet support it (missing moisture reading,
 * reading outside every configured band, missing contract price) — the SAME
 * guards services/api's `/goods-receipts/:id/post` enforces (payable.ts throws
 * `MissingMoistureError`/`MoistureOutOfBandError`/`CoreError`, which `error.ts`
 * maps to HTTP 422 with this exact `message`).
 */
export function computeLedgerPreview(input: LedgerPreviewInput): LedgerPreviewOutcome {
  const net = Number(input.netKg);
  if (!input.netKg || Number.isNaN(net) || net <= 0) {
    return { ok: false, error: 'Enter the weighbridge gross/tare readings first.' };
  }

  try {
    const result = payableCents({
      basis: input.basis,
      netKg: input.netKg,
      mcPct: input.mcPct,
      priceCentsPerTonne: input.priceCentsPerTonne == null ? undefined : BigInt(input.priceCentsPerTonne),
      bands: toCoreBands(input.bands),
    });
    return { ok: true, result: { ...result, dryKg: dryKg(input.netKg, input.mcPct) } };
  } catch (err) {
    if (err instanceof CoreError) return { ok: false, error: err.message, code: err.code };
    return { ok: false, error: err instanceof Error ? err.message : 'Unable to compute the payable amount.' };
  }
}

export interface EachLedgerPreviewResult {
  payableCents: bigint;
  usedPriceCents: bigint;
}

export type EachLedgerPreviewOutcome = { ok: true; result: EachLedgerPreviewResult } | { ok: false; error: string; code?: string };

export interface EachLedgerPreviewInput {
  /** Units received — a decimal string, whole-number-enforced below (mirrors
   * schemas/purchasing.ts's/schemas/intake.ts's qtyEachSchema/
   * positiveEachQtySchema; the server remains the actual enforcement point). */
  qty: string;
  priceCentsPerUnit: string | null;
}

/**
 * EACH (count) goods-receipt line ledger-effect preview — `payable = qty *
 * priceCentsPerUnit` (§-EACH, `@zebra/core`'s `eachPayableCents` — the SAME
 * pure function services/api's `/goods-receipts/:id/post` calls for a count
 * line). No moisture basis exists for a count line, so there's no dry-mass /
 * band-price analogue to `computeLedgerPreview`'s `dryKg` here — just the flat
 * multiply-and-round.
 */
export function computeEachLedgerPreview(input: EachLedgerPreviewInput): EachLedgerPreviewOutcome {
  const qty = Number(input.qty);
  if (!input.qty || Number.isNaN(qty) || qty <= 0) {
    return { ok: false, error: 'Enter the quantity received first.' };
  }
  if (!Number.isInteger(qty)) {
    return { ok: false, error: 'Quantity must be a whole number of units.' };
  }
  if (input.priceCentsPerUnit == null) {
    return { ok: false, error: 'This PO line has no price per unit.' };
  }

  try {
    const result = eachPayableCents(input.qty, BigInt(input.priceCentsPerUnit));
    return { ok: true, result: { payableCents: result, usedPriceCents: BigInt(input.priceCentsPerUnit) } };
  } catch (err) {
    if (err instanceof CoreError) return { ok: false, error: err.message, code: err.code };
    return { ok: false, error: err instanceof Error ? err.message : 'Unable to compute the payable amount.' };
  }
}
